import React, { useEffect, useId, useState } from "react";
import { ToolbarScrollIndicator } from "./ToolbarScrollIndicator";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  TextB,
  TextItalic,
  TextAUnderline,
  ListBullets,
  ListNumbers,
  ArrowClockwise,
  ArrowCounterClockwise,
  TextH,
  TextHOne,
  TextHTwo,
  TextHThree,
  PlayIcon,
  Table,
  Link as LinkIcon,
  Image as ImageIcon,
  Feather,
  BookOpen,
} from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";
import { Editor } from "@tiptap/react";
import executeUnexecutedQueriesOfWorkspace from "./services/executeUnexecutedQueriesOfWorkspace";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useWorkspaceData } from "../FpEditor/services/useWorkspaceData";
import { usePlainDocument } from "../FpEditor/services/editorSurface";
import { useSharingStatus } from "@/hooks/useSharing";
import { useQueryClient } from "@tanstack/react-query";
import fpFetch from "../../services/fpFetch";
import { BASE_API_URL } from "../../constants";
import WritingStyleModal from "./WritingStyleModal";
import { liveEditor } from "@/utils/liveEditor";
import { normalizeLinkUrl } from "../FpEditor/services/linkUrl";
import {
  clearProductInterval,
  setProductInterval,
} from "@/utils/productTimers";

const MenuBar = () => {
  const [editor, setEditor] = useState<Editor | null>(null);
  // The strip that scrolls in a narrow pane, for its scroll indicator.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const scrollerId = useId();
  const [, forceUpdate] = useState({});
  // Computed per render, not a mount-time snapshot: /reports/:id and
  // /artifacts/:id share this component, and a client-side navigation
  // between them must move the Run-all-queries affordance with it — a
  // Document's hidden query nodes have no visible boxes to act on
  // (#11548 round 16).
  const showRunAllQueryBtn = useLocation().pathname.startsWith("/reports/");
  const [hasUnexecutedQueries, setHasUnexecutedQueries] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [showLinkTextInput, setShowLinkTextInput] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showWritingStyleModal, setShowWritingStyleModal] = useState(false);

  const queries = useSelector((state: RootState) => state.fpAppV4Data.queries);
  const queryAnswers = useSelector(
    (state: RootState) => state.fpAppV4Data.queryAnswers
  );
  const workspaceId = useSelector(
    (state: RootState) => state.fpAppV4Data.workspaceId
  );
  const type = useSelector((state: RootState) => state.fpAppV4Data.type) || "";
  const { data: workspaceData } = useWorkspaceData(workspaceId || undefined, {
    refetchOnMount: false,
  });
  const { data: sharingData } = useSharingStatus(
    "workspace",
    workspaceId || null,
    false
  );
  const isViewer = sharingData?.current_user_permission === "view";
  const queryClient = useQueryClient();

  // Get writing style from workspace, default to "General"
  const writingStyle = (workspaceData?.report_style || "General") as
    | "General"
    | "Narrative";

  const plainDocument = usePlainDocument();
  // Report-only affordances: chat-created Documents share the report's
  // workspace type, so the type alone can't distinguish them — the plain
  // flag does (#11548 r19).
  const isReportType =
    !plainDocument &&
    (type === "private_doc_editor" || type === "private_doc_editor_template");

  const handleWritingStyleChange = async (style: "General" | "Narrative") => {
    if (isViewer) return;

    try {
      await fpFetch(`${BASE_API_URL}/workspaces/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          _id: workspaceId,
          report_style: style,
          type,
        }),
      });

      queryClient.invalidateQueries({ queryKey: ["workspaceData"] });
    } catch (error) {
      console.error("Failed to update writing style:", error);
    }
  };

  const checkForUnexecutedQueries = () => {
    if (!editor || !queries) {
      setHasUnexecutedQueries(false);
      return;
    }

    const queryNodes: { node: any; pos: number }[] = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "queryInput") {
        queryNodes.push({ node, pos });
      }
    });

    let numUnexecutedQueries = 0;
    for (const { node } of queryNodes) {
      const qid = node.attrs.qid;
      if (!qid || queryAnswers.find((queryAnswer) => queryAnswer.qid === qid)) {
        continue;
      }

      // Check if the query input has actual content in Redux state
      const query = queries[qid];
      if (!query) {
        // If query doesn't exist in Redux yet, treat as executable if there are no query answers
        numUnexecutedQueries++;
        continue;
      }

      if (!query.query?.trim()) {
        continue;
      }

      if (!query.startExecution) {
        numUnexecutedQueries++;
      }
    }

    setHasUnexecutedQueries(numUnexecutedQueries > 0);
  };

  useEffect(() => {
    // window.fp.editor is set by EditorMain AFTER this bar mounts when the
    // two render together (the chat document panel, #11548 r19), and window
    // properties never trigger a re-render — so poll until it appears
    // instead of sampling once at mount.
    const existing = (window as Window).fp?.editor;
    if (existing) {
      setEditor(existing);
      return;
    }
    // window.fp is a plain object EditorMain assigns after this bar mounts,
    // not a DOM node or an event source — there is nothing to subscribe to,
    // so this stays a poll, just on the product timer module.
    const poll = setProductInterval(() => {
      const found = (window as Window).fp?.editor;
      if (found) {
        setEditor(found);
        clearProductInterval(poll);
      }
    }, 100);
    return () => clearProductInterval(poll);
  }, []);

  useEffect(() => {
    if (editor) {
      const subscription = editor.on("transaction", () => {
        forceUpdate({});
        checkForUnexecutedQueries();
      });
      return () => {
        subscription.destroy();
      };
    }
  }, [editor]);

  // Check for unexecuted queries when queries or queryAnswers change. The
  // effect runs after the render that received them, and the check reads the
  // editor's live document, so it needs no delay (#12187).
  useEffect(() => {
    checkForUnexecutedQueries();
  }, [queries, queryAnswers, editor]);

  const handleOpenLinkModal = () => {
    if (!editor) return;
    const { state } = editor;
    const { from, to } = state.selection;
    const selectedText = state.doc.textBetween(from, to, "");
    const isTextSelected = selectedText.length > 0;
    const isLink = editor.isActive("link");

    // Show text input if no text is selected OR if editing an existing link
    setShowLinkTextInput(!isTextSelected || isLink);

    const previousUrl = editor.getAttributes("link").href;
    setLinkUrl(previousUrl || "");

    // If editing a link, get the full link text
    if (isLink) {
      editor.chain().extendMarkRange("link").run();
      const updatedState = editor.state;
      const { from: linkFrom, to: linkTo } = updatedState.selection;
      const fullLinkText = updatedState.doc.textBetween(linkFrom, linkTo, "");
      setLinkText(fullLinkText);
      // Restore the original selection. The bar polls its editor out of
      // the global once and keeps it; tiptap v3 throws on commands of a
      // destroyed one (#11904).
      liveEditor(editor)?.commands.setTextSelection({ from, to });
    } else if (!isTextSelected) {
      setLinkText("");
    } else {
      setLinkText("");
    }

    setShowLinkModal(true);
  };

  const handleApplyLink = () => {
    if (!editor) return;

    if (!linkUrl || linkUrl.trim() === "") {
      // Empty - remove link if currently on a link
      if (editor.isActive("link")) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
      }
      setShowLinkModal(false);
      setLinkUrl("");
      setLinkText("");
      return;
    }

    // A scheme when none is given, no trailing slash.
    const finalUrl = normalizeLinkUrl(linkUrl);

    if (showLinkTextInput) {
      // If text input is shown, we need to insert/update both text and link
      const textToUse = linkText.trim() || finalUrl;

      if (editor.isActive("link")) {
        // Editing existing link - completely replace it with new text and URL
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .command(({ tr, state }: { tr: any; state: any }) => {
            // Now we have the full link selected, get its range
            const { from: linkFrom, to: linkTo } = state.selection;

            // Delete the entire link and insert new text with new link
            tr.delete(linkFrom, linkTo);
            tr.insertText(textToUse, linkFrom);

            // Apply the link mark to the new text
            const newLinkMark = state.schema.marks.link.create({
              href: finalUrl,
            });
            tr.addMark(linkFrom, linkFrom + textToUse.length, newLinkMark);

            return true;
          })
          .run();
      } else {
        // No text selected - insert new text with link
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: textToUse,
            marks: [{ type: "link", attrs: { href: finalUrl } }],
          })
          .run();
      }
    } else {
      // Text was selected - just add link to selection
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: finalUrl })
        .run();
    }

    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  };

  const handleImageUpload = () => {
    if (!editor) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = false;

    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          editor
            .chain()
            .focus()
            .setImage({
              src: base64,
              alt: file.name || "Uploaded image",
            })
            .run();
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Failed to upload image:", error);
      }
    };

    input.click();
  };

  if (!editor) {
    return null;
  }

  const isEditable = editor.isEditable;
  return (
    <div className="relative w-full">
      <div
        ref={setScroller}
        id={scrollerId}
        data-formatting-bar-scroller=""
        // In a narrow pane it scrolls with no native bar (which macOS hides
        // until used, and which took height either way): the thumb below
        // overlays the bar's bottom edge instead (Gerry, 2026-10-13).
        className="fp-scroll-quiet w-full flex justify-between items-center overflow-x-auto"
      >
        {/* Never narrower than its controls: in a bar too narrow for them it
          scrolls from its first control instead of centering them past
          both ends, which cut Bold off at the left (#13194). */}
        <div className="flex flex-none items-center justify-center gap-1 text-[var(--fp-ink-2)] mx-auto min-w-[300px]">
          {/* Writing Style Button - Only show for reports and report templates */}
          {isReportType && (
            <>
              <Tooltip title={`Writing style: ${writingStyle}`}>
                <button
                  disabled={!isEditable || isViewer}
                  onClick={() => setShowWritingStyleModal(true)}
                  className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                    isViewer ? "opacity-50 cursor-not-allowed" : ""
                  } text-gray-600`}
                >
                  {writingStyle === "Narrative" ? (
                    <BookOpen size={16} />
                  ) : (
                    <Feather size={16} />
                  )}
                </button>
              </Tooltip>
              <div className="w-px h-5 bg-[var(--fp-border-strong)] mx-1" />
            </>
          )}
          <Tooltip title="Bold">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("bold")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <TextB size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Italic">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("italic")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <TextItalic size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Underline">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("underline")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <TextAUnderline size={16} />
            </button>
          </Tooltip>
          <div className="w-px h-5 bg-[var(--fp-border-strong)] mx-1" />
          <Tooltip title="Heading 1">
            <button
              disabled={!isEditable}
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("heading", { level: 1 })
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <TextHOne size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Heading 2">
            <button
              disabled={!isEditable}
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("heading", { level: 2 })
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <TextHTwo size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Heading 3">
            <button
              disabled={!isEditable}
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("heading", { level: 3 })
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <TextHThree size={16} />
            </button>
          </Tooltip>
          <div className="w-px h-5 bg-[var(--fp-border-strong)] mx-1" />
          <Tooltip title="Bullet List">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("bulletList")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <ListBullets size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Numbered List">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("orderedList")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <ListNumbers size={16} />
            </button>
          </Tooltip>
          <div className="w-px h-5 bg-[var(--fp-border-strong)] mx-1" />
          <Tooltip title="Insert link">
            <button
              disabled={!isEditable}
              onClick={handleOpenLinkModal}
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("link")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <LinkIcon size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Insert image">
            <button
              disabled={!isEditable}
              onClick={handleImageUpload}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-all text-gray-600"
            >
              <ImageIcon size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Insert table">
            <button
              disabled={
                !isEditable ||
                editor.isActive("tableCell") ||
                editor.isActive("tableHeader")
              }
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
              className={`p-1.5 rounded-md hover:bg-gray-100 transition-all ${
                editor.isActive("table")
                  ? "bg-gray-200 text-gray-800"
                  : "text-gray-600"
              }`}
            >
              <Table size={16} />
            </button>
          </Tooltip>
          <div className="w-px h-5 bg-[var(--fp-border-strong)] mx-1" />
          <Tooltip title="Undo">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().undo().run()}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-all text-gray-600"
            >
              <ArrowCounterClockwise size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Redo">
            <button
              disabled={!isEditable}
              onClick={() => editor.chain().focus().redo().run()}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-all text-gray-600"
            >
              <ArrowClockwise size={16} />
            </button>
          </Tooltip>
          {showRunAllQueryBtn && (
            <>
              <div className="w-px h-5 bg-[var(--fp-border-strong)] mx-1" />
              <Tooltip title="Run all queries">
                <button
                  className={`p-1.5 rounded-md transition-all ${
                    hasUnexecutedQueries
                      ? "hover:bg-gray-100 text-gray-700"
                      : "opacity-50 cursor-not-allowed text-gray-400"
                  }`}
                  disabled={!hasUnexecutedQueries}
                  onClick={() =>
                    hasUnexecutedQueries &&
                    executeUnexecutedQueriesOfWorkspace(null, () => {})
                  }
                >
                  <PlayIcon size={16} />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      <ToolbarScrollIndicator
        scroller={scroller}
        scrollerId={scrollerId}
        label="Formatting tools"
      />
      {/* Writing Style Modal */}
      <WritingStyleModal
        open={showWritingStyleModal}
        onClose={() => setShowWritingStyleModal(false)}
        currentStyle={writingStyle}
        onStyleChange={handleWritingStyleChange}
        disabled={isViewer}
      />
      {/* Link Modal - rendered via portal to document body */}
      {showLinkModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Insert Link</h3>
              {showLinkTextInput && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text
                  </label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        document
                          .querySelector<HTMLInputElement>(
                            'input[placeholder="https://example.com"]'
                          )
                          ?.focus();
                      }
                    }}
                    placeholder="Link text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    autoFocus
                  />
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL
                </label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleApplyLink();
                    } else if (e.key === "Escape") {
                      setShowLinkModal(false);
                      setLinkUrl("");
                      setLinkText("");
                    }
                  }}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  autoFocus={!showLinkTextInput}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowLinkModal(false);
                    setLinkUrl("");
                    setLinkText("");
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyLink}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-900"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MenuBar;
